// 261010 Epic 2 / P6 — phần hiển thị phiếu "AI chấm clip" trong danh sách CÁC BẢN CLIP của bước Video (không antd ⇒ jest render được; bước Video import antd nên không render được dưới jest).
// Chỉ HIỆN + gọi lại: tag kết luận, "AI đề xuất", "Chọn tạm (tự động)" / "Người đã chọn", nút "Chấm clip này", "Xem phiếu"; hộp ghi lý do khi chọn bản chưa sạch (KHÔNG chặn cứng).
// Dữ liệu + luật (nút khoá khi nào, ai được đề xuất, khi nào phải hỏi lý do) do bước Video tính bằng clipReviewPolicy / stepReviewPolicy rồi truyền xuống.
import React, { useEffect, useState } from 'react';
import { clipReviewChip, validateSelectReason, type AssemblyClipWarning, type ClipRowView } from './clipReviewPolicy';
import './stepReview.css';

export interface ClipReviewRowProps {
  /** Mọi dữ liệu của dòng do `clipRowsView` tính từ `clipBoards` của BE. */
  row: ClipRowView;
  onRun: () => void;
  onFocus: () => void;
}

export const ClipReviewRow: React.FC<ClipReviewRowProps> = ({ row, onRun, onFocus }) => {
  const chip = clipReviewChip(row.review);
  return (
    <div className="wf-clip-rv" data-testid="wf-clip-rv" aria-label={`Phiếu AI chấm ${row.label}`} aria-busy={row.sending ? 'true' : undefined}>
      <b>{row.label}</b>
      <span className={`wf-rv-chip ${chip.tone}`} title={chip.title}>{chip.label}</span>
      {row.blockingCount > 0 ? <span className="wf-rv-chip warn" title="Mức nặng chỉ là mức trên phiếu — không khoá chọn bản, ghép tập hay tạo video">{row.blockingCount} lỗi nặng — phiếu không chặn</span> : null}
      {row.recommended ? <span className="wf-rv-chip ok" title="AI gợi ý — bạn bấm chọn, AI không tự đổi bản">AI đề xuất</span> : null}
      {row.selection ? <span className={`wf-rv-chip ${row.selection.tone}`}>{row.selection.label}</span> : null}
      {row.usable === false ? <span className="wf-rv-chip muted" title="Clip này gen theo engine / số giây khác video — chọn bản này sẽ bị từ chối">Lệch engine / giây</span> : null}
      <span className="sp" />
      <button type="button" className="wf-btn" disabled={!row.run.enabled} title={row.run.reason} onClick={onRun}>{row.run.label}</button>
      {row.focused ? <span className="wf-rv-note">Đang xem phiếu</span> : <button type="button" className="wf-btn ghost" onClick={onFocus}>Xem phiếu</button>}
      {row.sending ? <div className="wf-rv-note wf-rv-wait" role="status"><span className="wf-rv-spin" aria-hidden="true" /> Đang tải clip và trích khung hình — có thể mất vài chục giây, đừng bấm lại.</div> : null}
      {row.detailLine ? <div className="wf-rv-note wf-clip-rv-line">{row.detailLine}</div> : null}
      {row.rankNote ? <div className="wf-rv-note wf-clip-rv-line">{row.rankNote}</div> : null}
    </div>
  );
};

/** Dòng "AI đề xuất Bản N — …" trên đầu danh sách bản clip. Không có đề xuất ⇒ không vẽ gì. */
export const ClipRecommendationNote: React.FC<{ line: string | null }> = ({ line }) => (line ? <div className="wf-rv-note wf-clip-reco" role="status" data-testid="wf-clip-reco">{line}</div> : null);

/** Bước Ghép: cảnh báo chữ về phiếu AI chấm của clip đang chọn ở từng video. KHÔNG nút, KHÔNG đổi nút / luồng ghép (chỉ hiện — User 25/10). */
export const AssemblyClipWarningList: React.FC<{ warnings?: AssemblyClipWarning[] }> = ({ warnings }) => (
  warnings && warnings.length > 0 ? (
    <div className="wf-alert WARN" data-testid="wf-asm-clip-warnings">
      <b>Phiếu AI chấm clip của các bản đang chọn (không chặn ghép):</b>
      <ul className="wf-list">{warnings.map((w) => <li key={w.eventId}>{w.text}</li>)}</ul>
    </div>
  ) : null
);

interface DialogProps {
  label: string; reasons: string[];
  /** true ⇒ lý do được ghi vào phiếu AI chấm của bản này (decide Chấp nhận + ghi chú); false ⇒ bản chưa có phiếu hợp lệ, lý do chỉ để xác nhận. */
  storable: boolean;
  busy: boolean; onConfirm: (note: string) => void; onCancel: () => void;
}

/**
 * Chọn bản có lỗi nặng / NEEDS_LEADER chưa phân xử, hoặc chưa chấm (Leader Rev 1): bắt GHI LÝ DO rồi cho chọn — không chặn cứng, không bắt phân xử trước.
 * BE không nhận `note` ở select-clip ⇒ lý do được ghi vào PHIẾU của bản đó bằng `decide` (khi có phiếu hợp lệ — `storable`); nói thật điều này với người dùng.
 */
export const SelectReasonDialog: React.FC<DialogProps> = ({ label, reasons, storable, busy, onConfirm, onCancel }) => {
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onCancel(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onCancel]);
  const title = `Chọn ${label} dù phiếu chưa sạch?`;
  const submit = () => {
    const bad = validateSelectReason(note);
    if (bad) { setError(bad); return; }
    onConfirm(note.trim());
  };
  return (
    <div className="wf-rv-modal" role="dialog" aria-modal="true" aria-label={title} data-testid="wf-select-reason" onClick={onCancel}>
      <div className="wf-rv-modal-in wf-select-reason" onClick={(e) => e.stopPropagation()}>
        <div className="wf-rv-modal-h"><b>{title}</b></div>
        <ul className="wf-select-reason-list">{reasons.map((r) => <li key={r}>{r}</li>)}</ul>
        <div className="wf-rv-note">Phiếu chỉ để xem — bạn vẫn chọn được bản này, nhưng hãy ghi lý do để người sau hiểu vì sao.</div>
        <div className="wf-rv-note">{storable
          ? 'Lý do sẽ được ghi vào phiếu AI chấm của bản này (phân xử "Chấp nhận kết luận").'
          : 'Bản này chưa có phiếu hợp lệ nên lý do chỉ để xác nhận, không được lưu lại.'}</div>
        <div className="wf-rv-dec">
          <label className="wf-rv-note" htmlFor="wf-select-reason-note">Lý do chọn bản này (bắt buộc)</label>
          <textarea id="wf-select-reason-note" value={note} disabled={busy} onChange={(e) => { setNote(e.target.value); setError(null); }} placeholder="Vd: lính thứ ba chỉ lướt qua ở rìa khung, chấp nhận được…" />
          {error ? <div className="wf-alert WARN" role="alert">{error}</div> : null}
          <div className="row">
            <button type="button" className="wf-btn primary" disabled={busy} onClick={submit}>Vẫn chọn bản này</button>
            <button type="button" className="wf-btn" onClick={onCancel}>Huỷ</button>
          </div>
        </div>
      </div>
    </div>
  );
};
